import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  InputAdornment,
  TextField,
} from "@mui/material";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import useStyles from "./styles";
import { Icon } from "../Icon/Icon";
import { colors } from "../../styles/colors";
import { ChangeEventHandler, SyntheticEvent } from "react";

/**
 * Default equality for `strictOptions`: options coming from the API are
 * objects with an id, and the value a form holds is usually a COPY of one
 * (rebuilt from the vehicle payload), never the same reference.
 */
const sameOption = (option: any, value: any) => {
  if (option && value && typeof option === "object" && typeof value === "object") {
    if ("id" in option && "id" in value) return option.id === value.id;
  }
  return option === value;
};

type Props = {
  placeholder?: string;
  label?: string;
  marginBottom?: string | number;
  options: readonly unknown[];
  width?: string | number;
  helperTextError?: string;
  value?: any
  defaultValue?: any
  getOptionLabel?: (option: unknown) => string;
  isOptionEqualToValue?: (option: unknown, value: unknown) => boolean;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: {},
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown> | undefined
  ) => void;
  onInputChange?: (
    event: SyntheticEvent<Element, Event>,
    value: string,
    reason: string
  ) => void;
  onChangeTextField?:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  /**
   * Only values from `options` can be chosen (opt-in, default false).
   *
   * The default `freeSolo` mode let a user TYPE "Renault" and move on: the
   * text stayed on screen, but no option was ever selected, so the form held
   * no brand and its Save button stayed disabled with nothing explaining why
   * (incident STC, 2026-11 - "je peux saisir, mais je n'ai pas accès à
   * l'enregistrement").
   *
   * In strict mode the first matching option is highlighted while typing and
   * is selected when the field loses focus, so typing the full name of a
   * listed brand IS selecting it. Text that matches nothing is cleared on
   * blur instead of pretending to be a value.
   */
  strictOptions?: boolean;
};

export const CustomSearch: React.FC<Props> = ({
  placeholder,
  label,
  marginBottom,
  options,
  value,
  defaultValue,
  getOptionLabel,
  isOptionEqualToValue,
  width,
  helperTextError,
  onChange,
  onInputChange,
  onChangeTextField,
  strictOptions = false,
}) => {
  const classes = useStyles({
    marginBottom,
    width,
    isError:
      helperTextError && helperTextError?.trim()?.length > 0 ? true : false,
  });

  return (
    <div className={classes.container}>
      {label && (
        <div className={classes.containerLabel}>
          <CustomTypography
            text={label}
            variant={"link"}
            textDecoration={"none"}
          />
        </div>
      )}

      <Autocomplete
        freeSolo={!strictOptions}
        autoHighlight={strictOptions}
        autoSelect={strictOptions}
        fullWidth
        disableClearable
        options={options}
        getOptionLabel={
          strictOptions
            ? (option: any) =>
                // A strict Autocomplete may be handed a null value while
                // options load, and MUI requires a string back.
                option === null || option === undefined
                  ? ""
                  : getOptionLabel
                  ? getOptionLabel(option) ?? ""
                  : String(option)
            : getOptionLabel
        }
        isOptionEqualToValue={
          isOptionEqualToValue ?? (strictOptions ? sameOption : undefined)
        }
        onChange={onChange}
        onInputChange={onInputChange}
        defaultValue={defaultValue}
        value={strictOptions ? value ?? null : value}
        renderInput={(params: any) => (
          <TextField
            className={classes.formControl}
            placeholder={placeholder}
            error={!!helperTextError}
            helperText={helperTextError}
            onChange={onChangeTextField}
            {...params}
            // SelectProps={{
            //   MenuProps: {
            //     anchorOrigin: {
            //       vertical: "bottom",
            //       horizontal: "left",
            //     },
            //     getContentAnchorEl: null,
            //   },
            // }}
            InputProps={{
              ...params.InputProps,
              // type: "search",
              endAdornment: (
                <InputAdornment position="end">
                  <Icon
                    name="search"
                    width={"20px"}
                    height={"20px"}
                    color={colors.darkblack}
                  />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </div>
  );
};
